import React from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import {
  TextField,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import AnimatedErrorMessage from "../animatedErrorMessage";

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface BaseSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  isMulti?: boolean;
  disabled?: boolean;
  dataElmId?: string;
  rules?: RegisterOptions;
  defaultValue?: string | number | SelectOption | SelectOption[];
}

// Shared sx styles for consistent appearance
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    "& fieldset": {
      borderColor: "#E2E8F0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0078D4",
      borderWidth: "1px",
    },
    "&.Mui-error fieldset": {
      borderColor: "#ED4F9D",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 14px",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    color: "#334155",
    fontWeight: 500,
    letterSpacing: "-0.01em",
    "&::placeholder": {
      color: "#8A99AE",
      opacity: 1,
    },
  },
  "& .MuiSelect-select": {
    padding: "16px 14px",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    color: "#334155",
    fontWeight: 500,
    letterSpacing: "-0.01em",
  },
};

const BaseSelect: React.FC<BaseSelectProps> = ({
  name,
  label,
  placeholder = "Select an option",
  options,
  isMulti = false,
  disabled = false,
  dataElmId,
  rules = {},
  defaultValue,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();

  const fieldError = (errors as any)[name];

  // For multi-select, use Autocomplete
  if (isMulti) {
    return (
      <div>
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue !== undefined ? defaultValue : []}
          rules={rules}
          render={({ field }) => (
            <Autocomplete
              {...field}
              multiple
              options={options}
              getOptionLabel={(option: SelectOption) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disabled={disabled}
              onChange={(_, newValue) => {
                field.onChange(newValue);
                if (fieldError && newValue) {
                  trigger(name);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  error={!!fieldError}
                  data-elm-id={dataElmId}
                  sx={textFieldSx}
                />
              )}
              sx={{
                "& .MuiAutocomplete-tag": {
                  fontSize: "14px",
                },
              }}
            />
          )}
        />
        <AnimatedErrorMessage
          show={!!fieldError}
          message={fieldError?.message?.toString()}
          variant="form"
        />
      </div>
    );
  }

  // For single select, use TextField with select prop
  return (
    <div>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue !== undefined ? defaultValue : ""}
        rules={rules}
        render={({ field }) => {
          // Handle SelectOption objects - extract the value
          const currentValue = typeof field.value === 'object' && field.value !== null
            ? field.value.value
            : field.value;

          return (
            <TextField
              {...field}
              select
              fullWidth
              label={label}
              disabled={disabled}
              error={!!fieldError}
              data-elm-id={dataElmId}
              value={currentValue ?? ""}
              onChange={(e) => {
                // Find the full option object to maintain compatibility
                const selectedOption = options.find(opt => opt.value === e.target.value);
                field.onChange(selectedOption || e.target.value);
                if (fieldError) {
                  trigger(name);
                }
              }}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected || selected === "") {
                      return <span style={{ color: "#8A99AE" }}>{placeholder}</span>;
                    }
                    const option = options.find(opt => opt.value === selected);
                    return option?.label || selected;
                  },
                },
              }}
              sx={textFieldSx}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    fontSize: "15px",
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      />
      <AnimatedErrorMessage
        show={!!fieldError}
        message={fieldError?.message?.toString()}
        variant="form"
      />
    </div>
  );
};

export default BaseSelect;
